import React, { useMemo } from 'react';
import { X, CornerDownRight, RefreshCw } from 'lucide-react';
import { OPERATIONS } from '../NewWorkflow/utils/operations'; // Adjust path if needed

const OperandSlot = ({ operand, fieldData, onClick, isSelected, registerRef }) => (
    <div 
        ref={el => registerRef(el)}
        onClick={onClick} 
        className={`flex-grow p-3 rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'border-indigo-500 bg-indigo-900/50' : 'border-dashed border-slate-600 hover:border-slate-500 bg-slate-900/50'}`}
    >
        {fieldData ? (
            <div>
                <div className="font-mono text-sm text-white truncate">{fieldData.display}</div>
                <div className="text-xs text-slate-400">{fieldData.dataType}</div>
            </div>
        ) : (
            <div className="text-slate-500 text-sm">{operand}</div>
        )}
    </div>
);

const OperationRow = ({ operation, allInputFields, onUpdate, onDelete, onSelectOperand, selectedOperandTarget, registerRef }) => {
    const operand1Field = allInputFields.find(f => f.path === operation.operand1Path);
    const operand2Field = allInputFields.find(f => f.path === operation.operand2Path);

    const availableOperators = Object.entries(OPERATIONS).filter(([_, op]) => 
        operand1Field && (op.appliesTo.includes('any') || op.appliesTo.includes(operand1Field.dataType.toLowerCase()))
    );
    const selectedOperator = OPERATIONS[operation.operator];
    const outputSchema = selectedOperator ? selectedOperator.getOutputSchema(operand1Field) : null;
    
    const isGlobalStateOperand = operand1Field && operand1Field.path.startsWith('globalState');
    const canUpdateGlobalState = useMemo(() => {
        return isGlobalStateOperand && selectedOperator?.preservesSchema;
    }, [isGlobalStateOperand, selectedOperator]);

    const handleToggleUpdate = (e) => {
        onUpdate({ ...operation, updateGlobalState: e.target.checked });
    };

    return (
        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 space-y-3 relative">
            <div className="flex items-center gap-3">
                <OperandSlot 
                    operand="Operand 1" 
                    fieldData={operand1Field}
                    onClick={() => onSelectOperand('operand1Path')}
                    isSelected={selectedOperandTarget?.operandKey === 'operand1Path'}
                    registerRef={(el) => registerRef(`operand-${operation.id}-1`, el)}
                />
                <div className="flex-shrink-0 w-48">
                    <select
                        value={operation.operator || ''}
                        onChange={(e) => onUpdate({ ...operation, operator: e.target.value, operand2Path: null, operand2DataType: null, updateGlobalState: false })}
                        disabled={!operand1Field}
                        className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white disabled:opacity-50"
                    >
                        <option value="" disabled>Select Operation...</option>
                        {availableOperators.map(([key, op]) => <option key={key} value={key}>{op.label}</option>)}
                    </select>
                </div>
                {selectedOperator && selectedOperator.getOperand2Types(operand1Field).length > 0 ? (
                    <OperandSlot 
                        operand="Operand 2" 
                        fieldData={operand2Field}
                        onClick={() => onSelectOperand('operand2Path')}
                        isSelected={selectedOperandTarget?.operandKey === 'operand2Path'}
                        registerRef={(el) => registerRef(`operand-${operation.id}-2`, el)}
                    />
                ) : <div className="flex-grow" /> /* Spacer */}
            </div>

            <div className="flex items-center gap-4 pt-3 border-t border-slate-700/50">
                <CornerDownRight size={20} className="text-slate-500 flex-shrink-0" />
                <div className="flex-grow flex items-center gap-3">
                    <label className="text-sm font-semibold text-slate-300">Output Name:</label>
                    <input
                        type="text"
                        value={operation.outputFieldName || ''}
                        onChange={(e) => onUpdate({ ...operation, outputFieldName: e.target.value })}
                        placeholder="e.g., total_sum"
                        className="flex-grow bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white"
                    />
                    <span className="bg-slate-600 text-slate-200 text-xs font-mono px-2 py-1 rounded-md capitalize">
                        {outputSchema?.dataType || '...'}
                    </span>
                </div>
                
                {isGlobalStateOperand && (
                    <div 
                        className="flex items-center gap-2 pl-4 border-l border-slate-700/50"
                        title={!canUpdateGlobalState ? "This operation changes the data structure or type, so it cannot update the Global State in-place." : "Update the Global State field with the result of this operation."}
                    >
                        <input
                            type="checkbox"
                            id={`update-global-${operation.id}`}
                            checked={!!operation.updateGlobalState}
                            onChange={handleToggleUpdate}
                            disabled={!canUpdateGlobalState}
                            className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <label 
                            htmlFor={`update-global-${operation.id}`} 
                            className={`flex items-center gap-1 text-sm font-semibold ${canUpdateGlobalState ? 'text-slate-300 cursor-pointer' : 'text-slate-500 cursor-not-allowed'}`}
                        >
                            <RefreshCw size={14} /> Update State
                        </label>
                    </div>
                )}
            </div>

            <button onClick={onDelete} className="absolute top-2 right-2 p-1 text-slate-500 hover:text-rose-500">
                <X size={16} />
            </button>
        </div>
    );
};

export default OperationRow;